import React from 'react'
import { Button } from '@/components/ui/button'
import { Link } from 'react-router-dom'
import { CheckCircle, Plus, FileText, Heart, Activity, Calendar } from 'lucide-react'
import type { User, HealthRecord } from '@/lib/types'

interface DashboardStats {
  totalRecords: number
  personalLogs: number
  medicalRecords: number
  recentActivity: HealthRecord[]
}

interface SimpleDashboardProps {
  user: User
  stats?: DashboardStats
}

const SimpleIndividualDashboard: React.FC<SimpleDashboardProps> = ({ user, stats }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="bg-white flex flex-col">
      <div className="flex-1 px-6 py-8">
        {/* Welcome Message */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">
            Welcome, {user.profile.firstName}
          </h1>
          <p className="text-gray-600 leading-relaxed">
            Your personal health dashboard is ready. Use the sidebar to manage your records and profile.
          </p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto mb-8">
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
              <div className="flex items-center gap-3 mb-2">
                <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
                <span className="text-sm text-gray-600">Total Records</span>
              </div>
              <p className="text-3xl font-bold text-gray-900">{stats.totalRecords}</p>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
              <div className="flex items-center gap-3 mb-2">
                <div className="h-10 w-10 rounded-lg bg-pink-100 flex items-center justify-center">
                  <Heart className="h-5 w-5 text-pink-600" />
                </div>
                <span className="text-sm text-gray-600">Personal Logs</span>
              </div>
              <p className="text-3xl font-bold text-gray-900">{stats.personalLogs}</p>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
              <div className="flex items-center gap-3 mb-2">
                <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <Activity className="h-5 w-5 text-green-600" />
                </div>
                <span className="text-sm text-gray-600">Medical Records</span>
              </div>
              <p className="text-3xl font-bold text-gray-900">{stats.medicalRecords}</p>
            </div>
          </div>
        )}

        {/* Recent Activity */}
        {stats && stats.recentActivity.length > 0 && (
          <div className="max-w-3xl mx-auto mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
            <div className="space-y-3">
              {stats.recentActivity.slice(0, 5).map((record) => (
                <div 
                  key={record.id} 
                  className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-100"
                >
                  <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                    record.type === 'personal' ? 'bg-blue-100' : 'bg-gray-100'
                  }`}>
                    {record.type === 'personal' ? (
                      <Heart className="h-5 w-5 text-blue-600" />
                    ) : (
                      <FileText className="h-5 w-5 text-gray-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {record.type === 'personal' 
                        ? (record.condition || 'Personal Health Log')
                        : (record.documentType || 'Medical Record')
                      }
                    </p>
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Calendar className="h-3 w-3" />
                      {formatDate(record.timestamp)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="flex flex-col items-center">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-lg w-full mb-8">
            <Button asChild className="h-12 text-center">
              <Link to="/app/add-record">
                <Plus className="h-4 w-4 mr-2" />
                Add Record
              </Link>
            </Button>
            <Button asChild variant="outline" className="h-12 text-center">
              <Link to="/app/records">
                View Records
              </Link>
            </Button>
          </div>

          {/* Status */}
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <span>Account active and ready</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SimpleIndividualDashboard